// Zuletzt gewählte Aufgabenansicht (Liste/Board/Gantt/Matrix, Gruppierung, Sortierung, Zoom) je Person und Projekt.
// Maßgeblich ist der Server (gilt auf jedem Gerät); der Browser hält eine Kopie, damit die Ansicht ohne Warten erscheint.
import { api } from './api';
import { optionenAusSuche, type AnsichtOptionen } from './aufgaben';

export type AufgabenAnsichtWahl = 'liste' | 'board' | 'gantt' | 'matrix';
export interface GemerkteAufgaben extends AnsichtOptionen { ansicht: AufgabenAnsichtWahl }

const ANSICHTEN: AufgabenAnsichtWahl[] = ['liste', 'board', 'gantt', 'matrix'];
const SPEICHERN_MS = 800;

/** Werte vom Server oder aus dem Browser prüfen; unbekannte Angaben fallen weg, ohne gültige Ansicht null. */
export function gemerkteAusWerten(w: unknown): GemerkteAufgaben | null {
  if (!w || typeof w !== 'object') return null;
  const o = w as Record<string, unknown>;
  if (!ANSICHTEN.includes(o.ansicht as AufgabenAnsichtWahl)) return null;
  const q = new URLSearchParams();
  for (const k of ['spalten', 'sortierung', 'zoom']) if (typeof o[k] === 'string') q.set(k, o[k] as string);
  return { ansicht: o.ansicht as AufgabenAnsichtWahl, ...optionenAusSuche(q.toString()) };
}

export const gleich = (a: GemerkteAufgaben | null, b: GemerkteAufgaben | null) =>
  !!a && !!b && a.ansicht === b.ansicht && a.spalten === b.spalten && a.sortierung === b.sortierung && a.zoom === b.zoom;

const schluessel = (email: string, projektId: string) => `aufgaben-ansicht:${email.toLowerCase()}:${projektId}`;
const speicher = new Map<string, GemerkteAufgaben>();
const laden = new Map<string, Promise<GemerkteAufgaben | null>>();
const geaendert = new Set<string>();   // seit dem Laden lokal gewählt: die (ältere) Antwort des Servers zählt dann nicht
const timer = new Map<string, ReturnType<typeof setTimeout>>();

/** Sofort verfügbarer Stand (Speicher bzw. Browser); null = in diesem Projekt noch nichts gemerkt oder noch nicht geladen. */
export function gemerkteAufgaben(email: string, projektId: string): GemerkteAufgaben | null {
  const k = schluessel(email, projektId);
  if (speicher.has(k)) return speicher.get(k)!;
  try {
    const g = gemerkteAusWerten(JSON.parse(localStorage.getItem(k) ?? 'null'));
    if (g) speicher.set(k, g);
    return g;
  } catch { return null; }
}

function ablegen(k: string, g: GemerkteAufgaben) {
  speicher.set(k, g);
  try { localStorage.setItem(k, JSON.stringify(g)); } catch { /* egal */ }
}

/** Stand vom Server holen (einmal je Projekt und Sitzung); Fehler ergeben einfach den Stand aus dem Browser. */
export function aufgabenAnsichtLaden(email: string, projektId: string): Promise<GemerkteAufgaben | null> {
  const k = schluessel(email, projektId);
  if (!email) return Promise.resolve(null);
  if (!laden.has(k))
    laden.set(k, api.ansichtLesen(projektId, 'aufgaben').then((r) => {
      const g = gemerkteAusWerten(r.werte);
      if (g && !geaendert.has(k)) ablegen(k, g);
      return gemerkteAufgaben(email, projektId);
    }).catch(() => gemerkteAufgaben(email, projektId)));
  return laden.get(k)!;
}

/** Neue Wahl merken: sofort im Browser, gebündelt auf dem Server. */
export function aufgabenAnsichtMerken(email: string, projektId: string, g: GemerkteAufgaben) {
  if (!email) return;
  const k = schluessel(email, projektId);
  if (gleich(speicher.get(k) ?? null, g)) return;
  geaendert.add(k);
  ablegen(k, g);
  clearTimeout(timer.get(k));
  timer.set(k, setTimeout(() => {
    timer.delete(k);
    api.ansichtSetzen(projektId, 'aufgaben', { ...g }).catch(() => { /* nur Komfort – beim nächsten Wechsel erneut */ });
  }, SPEICHERN_MS));
}
